import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import TableDisplay from '../../TableDisplay/TableDisplay';
import ContainerEntryForm from './EditContainerInfo';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { convertToLocalDateTimeInput } from '../../../utils/DateFormater';
import { useOptions } from "../../../hooks/useOptions";
import { Pencil, Trash2, Plus, Save, X, AlertTriangle } from 'lucide-react';
import GenericSelector from "../../UI/UXComponent/GenericSelector";
import { useTheme } from '../../../context/ThemeContext';
import { useConfirm } from '../../../context/ConfirmContext';
import { calculateDemurrage } from '../../../utils/DemurrageUtil';
import { toast } from 'react-toastify';

const CLIENT_PAGE_SIZE = 50;
const SERVER_PAGE_SIZE = 50;

export default function BillOfLandingInfo() {
    const { theme, isDark } = useTheme();
    const { confirm } = useConfirm();
    const { permissions } = useAuth();
    const { Id } = useParams();
    const decodedId = decodeURIComponent(Id); // will be "abc/ba"

    const location = useLocation();
    const editData = location.state?.data;
    const formRef = useRef();
    const [rows, setRows] = useState([]);
    const {
        suppliers,
        consignees,
        vessal: vesselList,
        logistics,
        shipping,
        refresh,
        status: statusOptions,
        loading: optionsLoading,
    } = useOptions();
    const [containersToAdd, setContainersToAdd] = useState([]);
    const [containersToEdit, setContainersToEdit] = useState({});
    const [isAddMode, setIsAddMode] = useState(false);
    const [containerData, setContainerData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [editingContainer, setEditingContainer] = useState(null);
    const [currentServerPage, setCurrentServerPage] = useState(1);
    const [loadedServerPages, setLoadedServerPages] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isBlModified, setIsBlModified] = useState(false);
    const [showPendingSection, setShowPendingSection] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pendingEditIndex, setPendingEditIndex] = useState(-1);
    
    // Check if any containers have custom FreeDays that differ from BoL defaults
    const hasCustomFreeDays = useMemo(() => {
        if (!editData || !editData.containers) return false;
        return editData.containers.some(c => 
            c.FreeDays !== null && c.FreeDays !== undefined && c.FreeDays !== editData.FreeDays
        );
    }, [editData]);

    // Check if any containers have custom status that differ from BoL defaults
    const hasCustomStatus = useMemo(() => {
        if (!editData || !editData.containers || !statusOptions.length) return false;
        const blStatusName = statusOptions.find(opt => opt.id === editData.status)?.name;
        if (!blStatusName) return false;
        return editData.containers.some(c => 
            c.state !== null && c.state !== undefined && c.state !== blStatusName
        );
    }, [editData, statusOptions]);

    const [formData, setFormData] = useState({
        billOfLadingNumber: "",
        consignee: "",
        vesselName: "",
        arrivalDate: "",
        supplier: "",
        Provider: "",
        shippingType: null,
        tax: 0,
        freeDays: "",   // BoL-level FreeDays
        status: "",     // BoL-level default status
    });

    // Define form fields configuration
    const formFields = useMemo(() => [
        {
            id: 'billOfLadingNumber',
            label: 'Bill of Lading Number',
            type: 'text',
            placeholder: 'Enter B/L Number',
            required: true,
            colSpan: 1,
            searchButton: true,
            customRender: (field) => (
                <div className="col-span-1 flex items-end gap-2">
                    <div className="flex-1">
                        <label htmlFor={field.id} className={`block text-sm font-medium ${theme.text} mb-1`}>
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type={field.type}
                            name={field.id}
                            id={field.id}
                            placeholder={field.placeholder}
                            value={formData[field.id]}
                            onChange={handleChange}
                            className={`w-full border rounded-md px-3 py-2 text-sm ${theme.border} ${theme.background} ${theme.text} placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            required={field.required}
                        />
                    </div>
                    {field.searchButton && (
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-sm text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 h-fit"
                            disabled={isLoading || !formData.billOfLadingNumber}
                        >
                            Search
                        </button>
                    )}
                </div>
            )
        },
        {
            id: 'Provider',
            label: 'Logistics provider',
            type: 'select',
            options: logistics,
            valueKey: 'id',
            refreshKey: 'logistics',
            addApi: 'logistics-providers',
            colSpan: 1
        },
        {
            id: 'consignee',
            label: 'Consignee',
            type: 'select',
            options: consignees,
            valueKey: 'id',
            refreshKey: 'consignees',
            addApi: 'setConsignee',
            colSpan: 1
        },
        {
            id: 'vesselName',
            label: 'Vessel Name',
            type: 'select',
            options: vesselList,
            valueKey: 'id',
            refreshKey: 'vessal',
            addApi: 'setVessal',
            colSpan: 1
        },
        {
            id: 'arrivalDate',
            label: 'Arrival Date',
            type: 'datetime-local',
            colSpan: 1,
            customRender: (field) => (
                <div>
                    <label htmlFor={field.id} className={`block text-sm font-medium ${theme.text} mb-1`}>
                        {field.label}
                    </label>
                    <input
                        type={field.type}
                        name={field.id}
                        id={field.id}
                        value={formData[field.id]}
                        onChange={handleChange}
                        className={`w-full border rounded-md px-3 py-2 text-sm ${theme.border} ${theme.background} ${theme.text} placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                </div>
            )
        },
        {
            id: 'supplier',
            label: 'Supplier',
            type: 'select',
            options: suppliers,
            valueKey: 'id',
            refreshKey: 'suppliers',
            addApi: 'setSupplier',
            colSpan: 1
        },
        {
            id: 'shippingType',
            label: 'Shipping Type',
            type: 'select',
            options: shipping,
            valueKey: 'id',
            refreshKey: 'shipping',
            addApi: 'setShippingDocument',
            colSpan: 1
        },
        {
            id: 'freeDays',
            label: 'Free Days',
            type: 'number',
            placeholder: 'e.g. 14',
            colSpan: 1,
            disabled: hasCustomFreeDays
        },
        {
            id: 'status',
            label: 'Default Status',
            type: 'select',
            options: statusOptions,
            valueKey: 'id',
            refreshKey: 'status',
            addApi: '',
            colSpan: 1,
            disabled: hasCustomStatus
        },
    ], [logistics, consignees, vesselList, suppliers, formData, isLoading, shipping, theme, statusOptions, hasCustomFreeDays, hasCustomStatus]);

    // Columns configuration
    const columns = useMemo(() => [
        { 
            key: "container_no", 
            label: "Container No", 
            sortable: true,
            width: '30%'
        },
        { 
            key: "status", 
            label: "Status",
            cellClassName: (value) => 
                value === "On port" ? 'bg-blue-100 text-blue-800' : 
                value === "Gate Pass" ? 'bg-green-100 text-green-800' : '',
            width: '20%'
        },
        { 
            key: "location", 
            label: "Location",
            width: '30%'
        },
        { 
            key: "weight", 
            label: "Weight",
            width: '20%'
        },
        { 
            key: "demurrage", 
            label: "Demurrage",
            width: '25%'
        }
    ], [theme]);

    // Transform API data to table format
    const transformData = useCallback((apiData) => {
        if (!apiData) return [];
        
        return apiData.map(c => ({
            container_no: c.containerNo || c.container_no || "",
            status: c.state || c.status || "",
            location: c.location || "",
            weight: c.weight || "",
            demurrage: editData?.ArrivalDate && (c.state || c.status) !== "In Transit" ? calculateDemurrage({
                ExcludeDayBitmask: editData.ExcludingDay,
                ArrivalDate: editData.ArrivalDate,
                FreeDay: c.FreeDays !== null && c.FreeDays !== undefined ? c.FreeDays : (c.bill_of_landing?.FreeDays ?? editData.FreeDays)
            }) : "",
            rawData: c // Store raw data for editing
        }));
    }, [editData]);

    function setFormDataOfBl(data){
        setFormData(prev => {
            const matchedProvider = logistics.find(
                (opt) => opt.name && data.provider && opt.name.toLowerCase() === data.provider.toLowerCase()
            );
            return {
                ...prev,
                arrivalDate: convertToLocalDateTimeInput(data.eventDateTime),
                Provider: matchedProvider ? matchedProvider.id : prev.Provider
            };
        });
    }

    const handleAddContainer = useCallback((data) => {
        setContainersToAdd(prev => [...prev, data]);
        setIsAddMode(false);
    }, []);

    const handleRemoveContainer = (index) => {
        setContainersToAdd(prev => prev.filter((_, i) => i !== index));
    };

    const handleEditPending = (index) => {
        const formDataObj = containersToAdd[index];
        const dataObj = Object.fromEntries(formDataObj.entries());
        dataObj.materials = formDataObj.getAll("materials");
        setEditingContainer(dataObj);
        setPendingEditIndex(index);
        setIsEditFormOpen(true);
    };

    // Fetch container data
    const fetchContainerData = useCallback(async (blNumber, offset = 0, limit = SERVER_PAGE_SIZE) => {
        const pageNum = Math.floor(offset / limit) + 1;
        if (loadedServerPages.has(pageNum)) return;
        
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_NETWORK}/track_and_trace`, 
                {
                    params: { 
                        bl: blNumber,
                    },
                    headers: { 
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        "skip_zrok_interstitial": "true", 
                    },
                     
                }
            );



            let data = response.data;
            if (data.length === 0) {
                setErrorMessage("No containers found for this B/L number");
            } else {
                if (typeof data === 'string') {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        console.error("Failed to parse response data:", e);
                        data = [];
                    }
                }
                setFormData({
                    billOfLadingNumber: blNumber,
                })
                const transformedData = transformData(data);

                if (data.length > 0){
                    setFormDataOfBl(data[0]);
                }
            
                setTotalItems(data.length || 0);
                setLoadedServerPages(prev => new Set(prev).add(pageNum));

                setContainersToAdd([]);
                const containerIds = [];
                transformedData.forEach(item => {
                    const newContainer = new FormData();
                    newContainer.append("bill_of_landing.BillOfLanding", blNumber || null)
                    newContainer.append("container_no", item.container_no || null)
                    containerIds.push(newContainer);
                });
                setContainersToAdd(containerIds);
            }
        } catch (error) {
            console.error("Failed to fetch containers:", error);
            setErrorMessage("Failed to fetch containers. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [loadedServerPages, transformData]);

    // Handle page changes
    const handlePageChange = useCallback((newClientPage, itemsPerPage) => {
        const startIndex = (newClientPage - 1) * itemsPerPage;
        const endIndex = newClientPage * itemsPerPage;
        
        const firstNeededPage = Math.floor(startIndex / SERVER_PAGE_SIZE) + 1;
        const lastNeededPage = Math.floor((endIndex - 1) / SERVER_PAGE_SIZE) + 1;
        
        for (let page = firstNeededPage; page <= lastNeededPage; page++) {
            if (!loadedServerPages.has(page)) {
                // fetchContainerData(formData.billOfLadingNumber, (page - 1) * SERVER_PAGE_SIZE, SERVER_PAGE_SIZE);
            }
        }
        
        setCurrentServerPage(firstNeededPage);
    }, [formData?.billOfLadingNumber, loadedServerPages]);

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        setIsBlModified(true);
    };

    // Search for bill of lading
    const handleSearch = useCallback(() => {
        if (!formData.billOfLadingNumber) return;
        
        setLoadedServerPages(new Set());
        // setRows([]);
        fetchContainerData(formData.billOfLadingNumber);
        setShowPendingSection(true);
    }, [formData?.billOfLadingNumber, fetchContainerData]);

    // Save bill of lading
    const handleSave = async () => {
        const payload = {
            BillOfLanding: formData.billOfLadingNumber,
            Consignee: formData.consignee || null,
            Vessel: formData.vesselName || null,
            ArrivalDate: formData.arrivalDate || null,
            Doc: formData.shippingType || null,
            Supplier: formData.supplier || null,
            Provider: formData.Provider || null,
            FreeDays: formData.freeDays !== "" ? Number(formData.freeDays) : null,
            status: formData.status !== "" ? Number(formData.status) : null,
            new_containers: containerData?.map(item => ({ container_no: item.container_no }))
        };
        const url = decodedId !== "new" ? 
            `${process.env.REACT_APP_NETWORK}/bills-of-lading/${decodedId}` :
            `${process.env.REACT_APP_NETWORK}/bills-of-lading`;

        try {
            await axios({
                method: decodedId !== "new" ? 'patch' : 'post',
                url: url,
                data: payload,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    "skip_zrok_interstitial": "true",
                },
                
            });

            return true;
        } catch (error) {
            return false;
        }
    };

    // Container CRUD operations
    const handleEdit = useCallback((row) => {  
        setEditingContainer(row.rawData);
        setPendingEditIndex(-1);
        setIsEditFormOpen(true);
    }, []);

    const handleDelete = async (containerId) => {
        const isConfirmed = await confirm("Are you sure you want to delete this container?");
        if (!isConfirmed) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_NETWORK}/containers/${containerId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` ,
                    "skip_zrok_interstitial": "true"
                },
            }

            );


            setRows(prev => prev.filter(item => item.rawData.container_id !== containerId));
        } catch (error) {
            console.error("Failed to delete container:", error);
            setErrorMessage("Failed to delete container.");
        }
    };

    const handleDeleteBl = async () => {
        const isConfirmed = await confirm("Delete this bill of landing?");
        if (!isConfirmed) return;
        
        try {
            await axios.delete(
                `${process.env.REACT_APP_NETWORK}/bills-of-lading/${decodedId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
                    "skip_zrok_interstitial": "true",
                },
                
            }
            );
        } catch (error) {
            console.error("Delete error:", error);
            setErrorMessage("Failed to delete bill of lading.");
        }
        finally{
            window.history.back();
        }
    };

    const handleEditFormClose = () => {
        setIsEditFormOpen(false);
        setEditingContainer(null);
        setPendingEditIndex(-1);
    };

    const handleSaveAll = async () => {
        // Validate required fields
        const missingFields = formFields.filter(f => f.required && (!formData[f.id] || String(formData[f.id]).trim() === ""));
        if (missingFields.length > 0) {
            const fieldLabels = missingFields.map(f => f.label).join(", ");
            toast.error(`Missing input field: ${fieldLabels}`);
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        try {
            if (isBlModified) {
                const blSaved = await handleSave();
                if (!blSaved) {
                    setErrorMessage("Failed to save Bill of Lading. Please check required fields.");
                    setIsSaving(false);
                    return;
                }
            }

            if (containersToAdd.length > 0) {
                const newContainerPromises = containersToAdd.map(async (container) => {
                    container.append("bill_of_landing.BillOfLanding", formData.billOfLadingNumber || null)
                    return axios.post(
                        `${process.env.REACT_APP_NETWORK}/containers`,
                        container,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data',
                                "skip_zrok_interstitial": "true",
                            },
                            
                        }
                    );
                });

                await Promise.all(newContainerPromises);
            }

            if (Object.keys(containersToEdit).length > 0) {
                const editPromises = Object.entries(containersToEdit).map(async ([id, container]) => {
                    return axios.patch(
                        `${process.env.REACT_APP_NETWORK}/containers/${id}/status`,
                        container,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data',
                                "skip_zrok_interstitial": "true",
                            },
                             
                        }
                    );
                });

                await Promise.all(editPromises);
            }

            setLoadedServerPages(new Set());
            setRows([]);
            setContainersToAdd([]);
            setContainersToEdit({});
            setIsBlModified(false);
            
            toast.success("All changes saved successfully!");
        } catch (error) {
            console.error("Save failed:", error);
            // HTTP 409 — FreeDays or status conflict between BoL and individual containers
            if (error.response?.status === 409) {
                setErrorMessage(
                    `Conflict: ${error.response.data?.detail || "Cannot update Bill of Lading value."}` +
                    " Some containers have custom values. Update them individually first, or reset all containers to the BoL value."
                );
            } else {
                setErrorMessage(error.response?.data?.message || "Failed to save changes. Please try again.");
            }
        } finally {
            setIsSaving(false);
            window.history.back();
        }
    };

    // Initialize form with edit data if available
    useEffect(() => {
        if (editData) {
            setFormData({
                billOfLadingNumber: editData.BillOfLanding,
                consignee: consignees.find((opt) => opt.name === editData.consignee_name)?.id || "",
                supplier: suppliers.find((opt) => opt.name === editData.supplier_name)?.id || "",
                arrivalDate: editData.ArrivalDate?.slice(0, 16) || "",
                Provider: logistics.find((opt) => opt.name === editData.provider_name)?.id || "",
                vesselName: vesselList.find((opt) => opt.name === editData.vessel_name)?.id || "",
                tax: editData.tax || 0,
                shippingType: shipping.find((opt) => opt.name === editData.Doc_name)?.id || "",
                freeDays: editData.FreeDays ?? "",  // BoL-level FreeDays
                status: editData.status ?? "",      // BoL-level default status
            });
            
            if (editData.containers) {
                const transformed = transformData(editData.containers);
                setRows(transformed);
                setContainerData(transformed);
            }
        }
    }, [editData, consignees, suppliers, logistics, vesselList, shipping, transformData]);

    // Render form field based on configuration
    const renderFormField = (field) => {
        if (field.customRender) {
            return field.customRender(field);
        }

        switch (field.type) {
            case 'select':
                return (
                    <div key={field.id}>
                        <label htmlFor={field.id} className={`block text-sm font-medium ${theme.text} mb-1`}>
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <GenericSelector
                            value={formData[field.id] || null}
                            onChange={(val) => {
                                setFormData(prev => {
                                    const newData = { ...prev, [field.id]: val };
                                    if (field.id === 'Provider' && val) {
                                        const selectedProvider = field.options?.find(opt => opt.id === val);
                                        if (selectedProvider && selectedProvider.freeDays !== undefined) {
                                            newData.freeDays = selectedProvider.freeDays !== null ? selectedProvider.freeDays : 0;
                                        }
                                    }
                                    return newData;
                                });
                                setIsBlModified(true);
                            }}
                            placeholder={`Select ${field.label}`}
                            options={field.options}
                            labelKey="name"
                            valueKey={field.valueKey}
                            onAddNew={() => refresh(field.refreshKey)}
                            addApi={field.addApi}
                            disabled={field.disabled}
                        />
                        {field.disabled && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                <AlertTriangle size={12} /> Locked: Individual container overrides exist.
                            </p>
                        )}
                    </div>
                );
            case 'text':
            case 'datetime-local':
            default:
                return (
                    <div key={field.id}>
                        <label htmlFor={field.id} className={`block text-sm font-medium ${theme.text} mb-1`}>
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type={field.type}
                            name={field.id}
                            id={field.id}
                            placeholder={field.placeholder}
                            value={formData[field.id]}
                            onChange={handleChange}
                            className={`w-full border rounded-md px-3 py-2 text-sm ${theme.border} ${theme.background} ${theme.text} placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            required={field.required}
                        />
                    </div>
                );
        }
    };

    return (
        <div className={`flex flex-col w-full h-full  ${theme.background} `}>
            <div className="flex justify-between items-center">
                <h1 className={`text-2xl font-bold ${theme.text}`}>Bill of Lading Management</h1>
                <div className="flex gap-1">
                    <button
                        onClick={handleSaveAll}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                        disabled={
                            isSaving || isLoading || (
                                !isBlModified && 
                                containersToAdd.length === 0 && 
                                Object.keys(containersToEdit).length === 0
                            )
                        }
                    >
                        <Save size={16} className="mr-2" />
                        {isSaving ? 'Saving...' : 'Save All Changes'}
                    </button>
                    {decodedId !== "new" && (
                        <button
                            onClick={handleDeleteBl}
                            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        >
                            <Trash2 size={16} className="mr-2" />
                            Delete B/L
                        </button>
                    )}
                </div>
            </div>

            {errorMessage && (
                <div className="flex items-center p-4 bg-red-100 text-red-700 rounded-md">
                    <AlertTriangle size={20} className="mr-2" />
                    {errorMessage}
                </div>
            )}

            {/* Form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {formFields.map(field => (
                    <div key={field.id} className={`col-span-${field.colSpan || 1}`}>
                        {renderFormField(field)}
                    </div>
                ))}
            </div>

            {/* Pending additions section - Simplified with toggle */}
            {containersToAdd.length > 0 && (
                <div className="bg-blue-50 p-3 rounded-md">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-blue-800">
                            Pending Containers to Add ({containersToAdd.length})
                        </h3>
                        <button 
                            onClick={() => setShowPendingSection(!showPendingSection)}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            {showPendingSection ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {showPendingSection && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {containersToAdd.map((container, index) => (
                                <div 
                                    key={`pending-${index}`} 
                                    className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm border border-blue-200"
                                >
                                    <span className="font-medium">{container.get("container_no")}</span>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handleEditPending(index)}
                                            className="text-blue-500 hover:text-blue-700"
                                            type="button"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                        <button 
                                            onClick={() => handleRemoveContainer(index)}
                                            className="text-red-500 hover:text-red-700"
                                            type="button"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Containers</h3>
            <div className={`flex flex-col w-full h-full  ${
        theme.background
      }`}>
                <TableDisplay 
                    key={`container-table-${rows.length}`}
                    columns={columns} 
                    data={rows.filter(Boolean)}
                    totalItems={totalItems}
                    title="container_no"
                    onDataChange={handleAddContainer}
                    userPermissions={["View_container_no", "Add"]}
                    itemsPerPage={CLIENT_PAGE_SIZE}
                    serverPageSize={SERVER_PAGE_SIZE}
                    isLoading={isLoading || optionsLoading}
                    onPageChange={handlePageChange}
                    theme={theme}
                    addDataComponent={
                        <ContainerEntryForm 
                            userPermissions={permissions}
                            mode="add"
                        />
                    }
                    addButtonText="Add Container"
                    onRowClick={(row) => { permissions.includes('Edit_Container') && handleEdit(row)}}
                    onAddButtonClick={() => setIsAddMode(true)}
                    isAddFormOpen={isAddMode}
                    height={'calc(40vh)'}
                    getRowClassName={(row) => {
                        if (row.demurrage && row.demurrage.includes("Overdue")) {
                            return isDark ? 'hover:bg-red-450 bg-red-400 text-red-50' : 'hover:bg-red-450 bg-red-400 text-red-50';
                        }
                        if (isDark) {
                            return 'hover:bg-slate-800 bg-slate-900 text-slate-200';
                        }
                        return `hover:bg-gray-100 bg-white ${theme.text}`;
                    }}
                />
            </div>

            {/* Edit Form Modal - Improved with better styling */}
            {isEditFormOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{pendingEditIndex !== -1 ? 'Edit Pending Container' : 'Edit Container'}</h2>
                            <button onClick={handleEditFormClose} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <ContainerEntryForm 
                                editData={editingContainer}
                            onSubmitSuccess={(data) => {
                                if (pendingEditIndex !== -1) {
                                    setContainersToAdd(prev => {
                                        const newArr = [...prev];
                                        newArr[pendingEditIndex] = data;
                                        return newArr;
                                    });
                                    setPendingEditIndex(-1);
                                } else {
                                    setContainersToEdit(prev => ({
                                        ...prev,
                                        [editingContainer.Container_ID]: data
                                    }));
                                }
                                handleEditFormClose();
                            }}
                            onCancel={handleEditFormClose}
                            userPermissions={permissions}
                            mode={pendingEditIndex === -1 ? "edit" : "add"}
                            handleDeleteFunction={pendingEditIndex === -1 ? handleDelete : null}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}